import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, CheckCircle } from 'lucide-react'
import evaluationGridService from '../../../services/evaluationGridService'
import classService from '../../../services/classService'
import studentService from '../../../services/studentService'
import evaluationService from '../../../services/evaluationService'

interface Score {
  criterionId: string
  score: number
}

interface StudentEval {
  studentId: string
  scores: Score[]
  comment: string
}

export function EvaluationSessionPage() {
  const { gridId, classId } = useParams<{ gridId: string; classId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [evaluations, setEvaluations] = useState<Record<string, StudentEval>>({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const { data: grid, isLoading: loadingGrid } = useQuery({
    queryKey: ['evaluationGrid', gridId],
    queryFn: () => evaluationGridService.getById(gridId!),
    enabled: !!gridId
  })

  const { data: classData, isLoading: loadingClass } = useQuery({
    queryKey: ['class', classId],
    queryFn: () => classService.getById(classId!),
    enabled: !!classId
  })

  const { data: students, isLoading: loadingStudents } = useQuery({
    queryKey: ['students', classId],
    queryFn: () => studentService.getByClass(classId!),
    enabled: !!classId
  })

  const createEvaluationMutation = useMutation({
    mutationFn: (data: any) => evaluationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] })
    }
  })

  // Initialiser les évaluations
  useEffect(() => {
    if (students && grid?.criteria) {
      const initial: Record<string, StudentEval> = {}
      students.forEach((student: any) => {
        initial[student.id] = {
          studentId: student.id,
          scores: grid.criteria.map((criterion: any) => ({
            criterionId: criterion.id,
            score: 0
          })),
          comment: ''
        }
      })
      setEvaluations(initial)
    }
  }, [students, grid])

  const updateScore = (studentId: string, criterionId: string, score: number) => {
    setEvaluations(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        scores: prev[studentId].scores.map(s =>
          s.criterionId === criterionId ? { ...s, score } : s
        )
      }
    }))
  }

  const updateComment = (studentId: string, comment: string) => {
    setEvaluations(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        comment
      }
    }))
  }

  const calculateFinalScore = (studentId: string) => {
    const studentEval = evaluations[studentId]
    if (!studentEval || !grid?.criteria) return 0

    let totalWeighted = 0
    let totalCoefficients = 0

    studentEval.scores.forEach(score => {
      const criterion = grid.criteria.find((c: any) => c.id === score.criterionId)
      if (criterion) {
        totalWeighted += (score.score / 4) * 20 * criterion.coefficient
        totalCoefficients += criterion.coefficient
      }
    })

    return totalCoefficients > 0 ? (totalWeighted / totalCoefficients).toFixed(1) : '0.0'
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Sauvegarder chaque évaluation
      for (const studentId of Object.keys(evaluations)) {
        const studentEval = evaluations[studentId]
        await createEvaluationMutation.mutateAsync({
          gridId,
          studentId,
          scores: studentEval.scores,
          comment: studentEval.comment,
          status: 'draft'
        })
      }
      setSaved(true)
      setTimeout(() => {
        navigate('/modules/evaluation/results')
      }, 1500)
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
    } finally {
      setSaving(false)
    }
  }

  if (loadingGrid || loadingClass || loadingStudents) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    )
  }

  if (!students || students.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={() => navigate('/modules/evaluation/evaluate')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>
          </div>
        </header>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <p className="text-gray-600 mb-4">Aucun élève dans cette classe</p>
            <button
              onClick={() => navigate('/students')}
              className="text-violet-600 hover:text-violet-700 font-medium"
            >
              Gérer les élèves
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/modules/evaluation/evaluate')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving || saved}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {saved ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Enregistré !</span>
                </>
              ) : saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Enregistrer tout</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{grid?.name}</h1>
          <p className="text-gray-600">{classData?.name} • {students.length} élève{students.length > 1 ? 's' : ''}</p>
        </div>

        <div className="space-y-4">
          {students.map((student: any) => (
            <div key={student.id} className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {student.firstName} {student.lastName}
                  </h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-violet-600">
                    {calculateFinalScore(student.id)}/20
                  </div>
                  <div className="text-xs text-gray-500">Note finale</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {grid?.criteria?.map((criterion: any) => {
                  const score = evaluations[student.id]?.scores.find(s => s.criterionId === criterion.id)?.score || 0
                  return (
                    <div key={criterion.id} className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {criterion.name} (coef. {criterion.coefficient})
                      </label>
                      <div className="flex gap-2">
                        {[0, 1, 2, 3, 4].map((value) => (
                          <button
                            key={value}
                            onClick={() => updateScore(student.id, criterion.id, value)}
                            className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                              score === value
                                ? 'bg-violet-500 text-white shadow-lg scale-105'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Commentaire</label>
                <textarea
                  value={evaluations[student.id]?.comment || ''}
                  onChange={(e) => updateComment(student.id, e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  rows={2}
                  placeholder="Remarques sur le comportement de l'élève..."
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || saved}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl font-medium disabled:opacity-50"
          >
            {saved ? (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Enregistré !</span>
              </>
            ) : saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Sauvegarde...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Enregistrer toutes les évaluations</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
